import { useTranslation } from 'react-i18next';
import { CheckRounded, Close, WarningAmberRounded } from '@mui/icons-material';
import { Button, IconButton, Tooltip, Typography } from '@mui/material';

import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { useIS32DetailsContextProvider } from 'features/systems/IS32/providers/IS32-details.provider';
import styles from 'features/systems/IS32/views/IS32-edit-configuration-header.module.scss';

const IS32EditConfigurationHeader = () => {
  const { t } = useTranslation('IS32');

  const {
    systemsDetailsHook: { selectedSystem },
  } = useSystemsContextProvider();

  const {
    selectedIS32ConfigurationUnit,
    updateIS32ConfigurationIsLoading,
    cancelIS32ConfigurationMutation,
    localbusUnitViewingMode,
    getDeviceAlertsForRoomUnit,
  } = useIS32DetailsContextProvider();

  const formatUnitName = () => {
    const displayType =
      selectedIS32ConfigurationUnit?.displayType && selectedIS32ConfigurationUnit.displayType.trim() !== ''
        ? selectedIS32ConfigurationUnit.displayType
        : null;
    const type =
      selectedIS32ConfigurationUnit?.type && selectedIS32ConfigurationUnit.type.trim() !== '' ? selectedIS32ConfigurationUnit.type : '';
    const serial = selectedIS32ConfigurationUnit?.serial ? `-${selectedIS32ConfigurationUnit.serial}` : '';
    const room =
      selectedIS32ConfigurationUnit?.room && selectedIS32ConfigurationUnit.room.trim() !== ''
        ? `-${selectedIS32ConfigurationUnit.room}`
        : '';
    return `${displayType ?? type}${serial}${room}`;
  };

  const saveButtonDisabled = localbusUnitViewingMode.some((unit) => unit === 'editing');

  const monitoringIcon = () => {
    const deviceAlerts = getDeviceAlertsForRoomUnit(selectedIS32ConfigurationUnit?.address ?? '');

    if (deviceAlerts.length > 0) {
      return (
        <Tooltip
          title={
            <>
              {deviceAlerts.length === 1 ? (
                <div>
                  {t(`monitoring.${deviceAlerts[0].type}`, {
                    address: selectedIS32ConfigurationUnit?.address ?? '',
                    location: deviceAlerts[0].location,
                  })}
                </div>
              ) : (
                <ul className={styles.alertList}>
                  {deviceAlerts.map((alert) => (
                    <li key={`alert-${alert.type}-${alert.reference}`}>
                      {t(`monitoring.${alert.type}`, {
                        address: selectedIS32ConfigurationUnit?.address ?? '',
                        location: alert.location,
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </>
          }
        >
          <WarningAmberRounded className={styles.malfunctionIcon} />
        </Tooltip>
      );
    }
  };

  return (
    <>
      <div className={styles.titleButtonsContainer}>
        <div className={styles.titleContainer}>
          <Typography variant="h2">
            {t('configuration.unitTitle', {
              address: selectedIS32ConfigurationUnit?.address,
              name: formatUnitName(),
            })}
          </Typography>
          {monitoringIcon()}
        </div>

        <div className={styles.buttonContainer}>
          <Button
            data-testid="deviceDetailCancelButton"
            variant="text"
            onClick={cancelIS32ConfigurationMutation}
            disabled={updateIS32ConfigurationIsLoading}
          >
            {t('actions.cancel')}
          </Button>

          <Button
            data-testid="deviceDetailSubmitButton"
            startIcon={<CheckRounded />}
            variant="contained"
            type="submit"
            loading={updateIS32ConfigurationIsLoading}
            loadingPosition="start"
            disabled={saveButtonDisabled}
          >
            {t('actions.save')}
          </Button>

          <IconButton
            aria-label="close"
            className={styles.closeButton}
            onClick={() => cancelIS32ConfigurationMutation()}
            data-testid="devicePopupCloseButton"
          >
            <Close />
          </IconButton>
        </div>
      </div>
      <Typography variant="subtitle1">{selectedSystem?.name}</Typography>
    </>
  );
};

export default IS32EditConfigurationHeader;
